import React, { useEffect, useState, useContext } from 'react';
import apiClient from '../api/client';
import { UserContext } from "./UserContext";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './AddCustomerForm.css';
import FormHeader from '../FormHeader';
import InternetCustomerForm from "./InternetCustomerForm";

const AddCustomerForm = () => {
  const { user, token } = useContext(UserContext);
  const navigate = useNavigate();

  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  /*
   * Customer type is only required when the selected operator
   * has Internet services enabled.
   *
   * null       -> not selected yet
   * "internet" -> Internet Customer
   * "normal"   -> Normal Customer
   */
  const [customerType, setCustomerType] = useState(null);

  const role = user.role;

  const [form, setForm] = useState({
    username: '',
    password: '',
    name: '',
    lastName: '',
    MobileNumber: '',
    EmailId: '',
    address: '',

    operatorDetails:
      role === "operator"
        ? `${user.operatorName || ''} - ${user.phoneNumber || ''}`
        : role === "admin"
          ? `${user.operatorName || ''} - ${user.operatorPhoneNumber || ''}`
          : "",

    autoplay: 'on',

    operatorId:
      role === "operator"
        ? user.operatorId
        : role === "admin"
          ? user.operatorId
          : "",

    remarks: '',
  });

  // ---------------------------------------------------------------
  // FETCH OPERATORS
  // ---------------------------------------------------------------

  const fetchOperators = async () => {
    setLoading(true);

    try {
      const { data } = await apiClient.get('/operator_api.php');
      setOperators(data.operators || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (role !== "operator") {
      fetchOperators();
    }
  }, [role]);

  // ---------------------------------------------------------------
  // CHECK WHETHER SELECTED OPERATOR HAS INTERNET ENABLED
  // ---------------------------------------------------------------

  const isInternetEnabled = () => {
    /*
     * Operator logged in directly.
     */
    if (role === "operator") {
      return !!user.internetEnabled;
    }

    /*
     * No operator selected yet.
     */
    if (!form.operatorId) {
      return false;
    }

    /*
     * Admin selecting their own operator.
     */
    if (Number(form.operatorId) === Number(user.operatorId)) {
      return !!user.internetEnabled;
    }

    /*
     * Admin/Superadmin selecting another operator.
     */
    const selectedOp = operators.find(
      (op) => Number(op.id) === Number(form.operatorId)
    );

    return !!selectedOp?.internetEnabled;
  };

  const internetGateActive = isInternetEnabled();

  // ---------------------------------------------------------------
  // RESET CUSTOMER TYPE
  // ---------------------------------------------------------------

  const resetCustomerType = () => {
    setCustomerType(null);
  };

  // ---------------------------------------------------------------
  // HANDLE INPUT CHANGES
  // ---------------------------------------------------------------

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedForm = {
      ...form,
      [name]: value,
    };

    /*
     * Keep the existing behavior for normal customer creation.
     *
     * We are NOT using this for Internet portal lookup anymore.
     */
    if (name === "MobileNumber") {
      updatedForm.username = value;
      updatedForm.password = value;
    }

    /*
     * Operator changed.
     *
     * Internet availability depends on the selected operator,
     * so reset the customer type selection.
     */
    if (name === "operatorId") {
      resetCustomerType();

      const selectedId = Number(value);

      /*
       * Admin can select:
       * 1. Their own operator
       * 2. Operators returned by API
       */
      const allOptions =
        role === "admin"
          ? [
              {
                id: Number(user.operatorId),
                operatorName: user.operatorName,
                phoneNumber: user.operatorPhoneNumber,
              },
              ...operators,
            ]
          : operators;

      const selectedOp = allOptions.find(
        (op) => Number(op.id) === selectedId
      );

      updatedForm = {
        ...updatedForm,
        operatorId: selectedId,
        operatorDetails: selectedOp
          ? `${selectedOp.operatorName} - ${selectedOp.phoneNumber}`
          : "",
      };
    }

    setForm(updatedForm);

    /*
     * Clear validation error for changed field.
     */
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  // ---------------------------------------------------------------
  // NORMAL CUSTOMER VALIDATION
  // ---------------------------------------------------------------

  const validateForm = () => {
    let newErrors = {};

    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[A-Za-z\s]+$/;

    // Name
    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    } else if (!nameRegex.test(form.name.trim())) {
      newErrors.name = "Name can only contain letters and spaces";
    }

    // Last Name
    if (!form.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    } else if (!nameRegex.test(form.lastName.trim())) {
      newErrors.lastName =
        "Last Name can only contain letters and spaces";
    }

    // Mobile
    if (!form.MobileNumber) {
      newErrors.MobileNumber = "Phone is required";
    } else if (!phoneRegex.test(form.MobileNumber)) {
      newErrors.MobileNumber = "Phone must be 10 digits";
    }

    // Email
    if (form.EmailId && !emailRegex.test(form.EmailId)) {
      newErrors.EmailId = "Invalid email format";
    }

    // Operator
    if (!form.operatorId && role !== "operator") {
      newErrors.operatorId = "Please select an operator";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors before submitting.");
      return false;
    }

    return true;
  };

  // ---------------------------------------------------------------
  // NORMAL CUSTOMER SUBMIT
  // ---------------------------------------------------------------

  const handleSubmit = async () => {
    /*
     * Run validation first.
     */
    if (!validateForm()) {
      return;
    }

    try {
      await apiClient.post(
        '/allCustomers.php',
        {
          ...form,
        }
      );

      toast.success('Customer added successfully!');

      navigate('/customers', {
        replace: true,
      });
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Customer already exists.');
      } else if (err.response?.status === 400) {
        toast.warn('Missing required fields.');
      } else {
        toast.error(
          'Failed to add customer. Please try again later.'
        );
      }
    }
  };

  // ---------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------

  return (
    <>
      <FormHeader />

      {loading ? (
        <div className="dashboard-loading">
          <div className="loader"></div>
          <p>Loading...</p>
        </div>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <div
          className={`form-container ${
            Object.keys(errors).length > 0
              ? "form-container-error"
              : ""
          }`}
        >
          <h2>Add Customer</h2>

          {/* -----------------------------------------------------
              OPERATOR SELECTION
              ----------------------------------------------------- */}

          {role !== "operator" ? (
            <>
              <select
                name="operatorId"
                value={form.operatorId}
                className={
                  errors.operatorId
                    ? "error-input"
                    : ""
                }
                onChange={handleChange}
              >
                <option value="">
                  -- Select Operator/Admin --
                </option>

                {role === "admin" && (
                  <option
                    key={`admin-${user.operatorId}`}
                    value={user.operatorId}
                  >
                    {user.operatorName} -{" "}
                    {user.phoneNumber}
                  </option>
                )}

                {operators.map((o) => (
                  <option
                    key={o.id}
                    value={o.id}
                  >
                    {o.operatorName} -{" "}
                    {o.phoneNumber}
                  </option>
                ))}
              </select>

              {errors.operatorId && (
                <p className="error-text">
                  {errors.operatorId}
                </p>
              )}
            </>
          ) : (
            <input
              name="operatorDetails"
              placeholder="Operator Details"
              value={`${user.operatorName || ''} - ${
                user.phoneNumber || ''
              }`}
              readOnly
            />
          )}

          {/* -----------------------------------------------------
              INTERNET ENABLED OPERATOR
              
              Instead of checking the customer's phone number,
              simply ask what type of customer is being added.
              ----------------------------------------------------- */}

          {internetGateActive && !customerType && (
            <div className="customer-type-selection">

              <p className="customer-type-title">
                🌐 Internet services are enabled.
              </p>

              <p className="customer-type-description">
                Please select the type of customer you want to add.
              </p>

              <div className="customer-type-buttons">

                <button
                  type="button"
                  className="customer-type-btn internet-customer-btn"
                  onClick={() =>
                    setCustomerType("internet")
                  }
                >
                  🌐 Internet Customer
                </button>

                <button
                  type="button"
                  className="customer-type-btn normal-customer-btn"
                  onClick={() =>
                    setCustomerType("normal")
                  }
                >
                  👤 Normal Customer
                </button>

              </div>
            </div>
          )}

          {/* -----------------------------------------------------
              INTERNET CUSTOMER

              Show the existing InternetCustomerForm when the
              operator supports Internet and the user selected
              Internet Customer.
              ----------------------------------------------------- */}

          {internetGateActive &&
            customerType === "internet" && (
              <div className="internet-customer-section">

                <InternetCustomerForm
                  mode="new"
                  operatorId={form.operatorId}
                  token={token}
                  onSuccess={() =>
                    navigate("/customers", {
                      replace: true,
                    })
                  }
                  onCancel={resetCustomerType}
                />

              </div>
            )}

          {/* -----------------------------------------------------
              NORMAL CUSTOMER

              Show when:
              
              1. Operator is NOT Internet enabled
                 OR
              2. Operator IS Internet enabled but user selected
                 "Normal Customer"
              ----------------------------------------------------- */}

          {(!internetGateActive ||
            customerType === "normal") && (
            <>
              {/* -------------------------------------------------
                  NORMAL CUSTOMER FIELDS
                  ------------------------------------------------- */}

              <input
                name="name"
                placeholder="Name"
                value={form.name}
                onChange={handleChange}
                className={
                  errors.name
                    ? "error-input"
                    : ""
                }
              />

              {errors.name && (
                <p className="error-text">
                  {errors.name}
                </p>
              )}

              <input
                name="lastName"
                placeholder="Last Name"
                value={form.lastName}
                onChange={handleChange}
                className={
                  errors.lastName
                    ? "error-input"
                    : ""
                }
              />

              {errors.lastName && (
                <p className="error-text">
                  {errors.lastName}
                </p>
              )}

              <input
                name="MobileNumber"
                placeholder="Phone"
                value={form.MobileNumber}
                onChange={handleChange}
                className={
                  errors.MobileNumber
                    ? "error-input"
                    : ""
                }
              />

              {errors.MobileNumber && (
                <p className="error-text">
                  {errors.MobileNumber}
                </p>
              )}

              <input
                name="EmailId"
                placeholder="Email Id"
                value={form.EmailId}
                onChange={handleChange}
                className={
                  errors.EmailId
                    ? "error-input"
                    : ""
                }
              />

              {errors.EmailId && (
                <p className="error-text">
                  {errors.EmailId}
                </p>
              )}

              <input
                name="address"
                placeholder="Address"
                value={form.address}
                onChange={handleChange}
              />

              <input
                name="remarks"
                placeholder="Remarks"
                value={form.remarks}
                onChange={handleChange}
              />

              {/* -------------------------------------------------
                  BUTTONS
                  ------------------------------------------------- */}

              <div className="add-cancel">

                <button
                  type="button"
                  className="add-customer-btn normal-add-btn"
                  onClick={handleSubmit}
                >
                  Add Customer
                </button>

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() =>
                    navigate('/customers', {
                      replace: true,
                    })
                  }
                >
                  Cancel
                </button>

              </div>
            </>
          )}

          {/* -----------------------------------------------------
              OPTIONAL: CUSTOMER TYPE BACK BUTTON

              When an Internet-enabled operator has selected either
              customer type, allow them to go back and choose again.

              This does NOT appear before the selection.
              ----------------------------------------------------- */}

          {internetGateActive &&
            customerType && (
              <div
                style={{
                  marginTop: "15px",
                  textAlign: "center",
                }}
              >
                <button
                  type="button"
                  className="change-btn"
                  onClick={resetCustomerType}
                >
                  ← Change Customer Type
                </button>
              </div>
            )}

        </div>
      )}
    </>
  );
};

export default AddCustomerForm;